import * as yup from "yup";
import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useCallback, useMemo} from "react";
import {useSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import {useMutation, useQueryClient} from "react-query";
import {useOnboardingHandling} from "./useOnboardingHandling";
import {OnboardingCallDaten} from "../../api";
import {useStarteOnboardingCall} from "../../client/hooks";

export function useOnboardingForm(): FormikConfig<OnboardingCallDaten> {
    const {translation, getCountryCode} = useOnboardingHandling();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const {mutateAsync} = useOnboardingCallStarten();

    const schema = yup.object().shape({
        nachname: yup.string().required(capitalize(translation('onboarding-attributes-last-name-required-message'))),
        vorname: yup.string().required(capitalize(translation('onboarding-attributes-first-name-required-message'))),
        firma: yup.string().required(capitalize(translation('onboarding-attributes-company-required-message'))),
        webseite: yup.string().nullable(),
        branche: yup.string().required(capitalize(translation('onboarding-attributes-company-industry-required-message'))),
        email: yup.string().email(capitalize(translation('onboarding-attributes-email-validation-failed-message'))).required(capitalize(translation('onboarding-attributes-email-required-message'))),
        aufgabeId: yup.string().required(capitalize(translation('onboarding-attributes-use-case-required-message'))),
        telefonnummer: yup.string()
            .matches(/^[1-9][0-9]*$/, capitalize(translation('onboarding-attributes-phone-validation-failed-message'))).required(capitalize(translation('onboarding-attributes-phone-required-message'))),
        telefonnummerBestaetigt: yup.boolean().required(capitalize(translation('confirmation-phone-required'))),
        datenschutzBestaetigt: yup.boolean().required(capitalize(translation('confirmation-data-privacy-required'))),
    });

    const initialTouched = useMemo(() => ({
        nachname: false,
        vorname: false,
        firma: false,
        webseite: false,
        branche: false,
        email: false,
        aufgabeId: false,
        telefonnummer: false,
        telefonnummerBestaetigt: false,
        datenschutzBestaetigt: false,
    }), []);

    const initialValues = useMemo(() => ({
        nachname: '',
        vorname: '',
        firma: '',
        webseite: '',
        branche: '',
        email: '',
        aufgabeId: '',
        telefonnummer: '',
        telefonnummerBestaetigt: false,
        datenschutzBestaetigt: false,
    }), []);

    const onSubmitCreateCallForm = useCallback(async (
        daten: OnboardingCallDaten,
        {setSubmitting, setStatus}: FormikHelpers<OnboardingCallDaten>
    ): Promise<void> => {
        try {
            await mutateAsync({
                nachname: daten.nachname,
                vorname: daten.vorname,
                firma: daten.firma,
                webseite: daten.webseite,
                branche: daten.branche,
                email: daten.email,
                aufgabeId: daten.aufgabeId,
                telefonnummer: `${getCountryCode()}${daten.telefonnummer?.trim()}`,
                telefonnummerBestaetigt: daten.telefonnummerBestaetigt,
                datenschutzBestaetigt: daten.datenschutzBestaetigt,
            });
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('sending-successful')), {
                variant: "success",
            });
            navigate(`/testanruf`);
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('sending-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation, navigate, getCountryCode]);

    return useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: true,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitCreateCallForm
        }),
        [schema, initialValues, initialTouched, onSubmitCreateCallForm]
    );
}

function useOnboardingCallStarten() {
    const starteOnboardingCall = useStarteOnboardingCall();
    const queryClient = useQueryClient();

    return useMutation(
        (input: OnboardingCallDaten): Promise<void> => {
            return starteOnboardingCall(input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["onboarding"]);
            },
        }
    );
}
